/**
 * Custom hook to handle individual investor organization submission
 * Consolidates create and update org mutation logic with Regtank modal flow
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  KYCStatus,
  RegtankOnboardingStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { countryNameToCodeMap } from "@funded-here-interface/common/src/constant/country";
import {
  setKycStatus,
  setOrgId,
} from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import useCreateOrg from "../../../hooks/useCreateOrg";
import useUpdateOrg from "../../../hooks/useUpdateOrg";
import {
  buildIndividualInvestorBankObject,
  buildIndividualInvestmentPreferences,
  buildCreateIndividualInvestorOrgData,
  buildUpdateIndividualInvestorOrgData,
  IndividualInvestorProfState,
  IndividualInvestorBankState,
  IndividualInvestorDocumentsUpload,
} from "../utils/individualInvestorOrgDataBuilder";
import { useRegtankValidation } from "../../Login/hooks/useRegtankValidation";
import { UserDetail } from "../../Login/hooks/useUserDataSync";

interface UseIndividualInvestorOrgSubmissionParams {
  setIsLoading: (loading: boolean) => void;
  onError: (error: Error) => void;
  setRegtankOnboardingUrl: (url: string) => void;
  handleNavigateNext: (
    userDetail: UserDetail,
    isValidOnboardingLink: boolean,
    openModal: () => void
  ) => "NO_ACTION_REQUIRED" | void;
}

/**
 * Success handler callback interface
 */
interface SuccessHandlerParams {
  userDetail: UserDetail;
  openRegtankModal: () => void;
}

/**
 * Hook to handle individual investor organization submission (create or update)
 */
export const useIndividualInvestorOrgSubmission = ({
  setIsLoading,
  onError,
  setRegtankOnboardingUrl,
  handleNavigateNext,
}: UseIndividualInvestorOrgSubmissionParams) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { validateAndSetRegtankUrl } = useRegtankValidation();
  const createOrgMutation = useCreateOrg();
  const updateOrgMutation = useUpdateOrg();

  /**
   * Handle post-submission success with Regtank modal or navigation
   * Consolidates duplicate success handler logic
   */
  const handleSubmissionSuccess = useCallback(
    ({ userDetail, openRegtankModal }: SuccessHandlerParams) => {
      setIsLoading(false);
      // Validate and set Regtank URL if applicable
      const isValidOnboardingLink =
        (userDetail?.regtankRequest?.length ?? 0) > 0
          ? validateAndSetRegtankUrl(userDetail, setRegtankOnboardingUrl)
              .isValid
          : false;

      const regtankApproved =
        userDetail?.regtankOnboardingFeatureFlag &&
        userDetail?.regtankRequest &&
        userDetail.regtankRequest.length > 0 &&
        userDetail.regtankRequest[0].onboardingStatus ===
          RegtankOnboardingStatusEnum.APPROVED;

      // For pending_approval approved Regtank, skip navigation handler and go directly to declaration
      if (regtankApproved && userDetail.declarationAISubmitted === false) {
        navigate("/declaration-ai", {
          state: { isPendingResubmission: true },
        });
      }
      // Show Regtank modal if feature is enabled and onboarding required
      else {
        handleNavigateNext(userDetail, isValidOnboardingLink, openRegtankModal);
      }
    },
    [
      navigate,
      setIsLoading,
      handleNavigateNext,
      setRegtankOnboardingUrl,
      validateAndSetRegtankUrl,
    ]
  );

  /**
   * Submit individual investor organization (create or update based on KYC status)
   */
  const submitIndividualInvestorOrganization = useCallback(
    ({
      kycStatus,
      orgId,
      token,
      userFirstName,
      userLastName,
      profState,
      bankState,
      documentsUpload,
      openRegtankModal,
      userDetail,
    }: {
      kycStatus: string | null;
      orgId: string | null;
      token: string;
      userFirstName: string;
      userLastName: string;
      profState: IndividualInvestorProfState;
      bankState: IndividualInvestorBankState;
      documentsUpload: IndividualInvestorDocumentsUpload;
      openRegtankModal: () => void;
      userDetail: UserDetail;
    }) => {
      // Build bank object
      const bank = buildIndividualInvestorBankObject(bankState);

      // Build investment preferences
      const investmentPreferences = buildIndividualInvestmentPreferences(
        profState.investmentAmount
      );

      // Get country code
      let countryCode: string | null = null;
      if (profState.countryOfResidence) {
        countryCode =
          countryNameToCodeMap.get(profState.countryOfResidence) || null;
      }

      setIsLoading(true);

      const baseParams = {
        profState,
        bank,
        token,
        userFirstName,
        userLastName,
        countryCode,
        documentsUpload,
        investmentPreferences,
      };

      const successHandlerParams: SuccessHandlerParams = {
        userDetail,
        openRegtankModal,
      };

      // If KYC is pending resubmission, update existing org
      if (kycStatus === KYCStatus.PENDING_RESUBMISSION) {
        const updateData = buildUpdateIndividualInvestorOrgData(baseParams);

        updateOrgMutation.mutate(
          {
            id: orgId?.toString() || "",
            data: updateData,
          },
          {
            onSuccess: (data) => {
              if (data?.kycStatus) {
                dispatch(setKycStatus({ kycStatus: data.kycStatus }));
              }
              handleSubmissionSuccess({
                ...successHandlerParams,
                userDetail: {
                  ...successHandlerParams.userDetail,
                  kycStatus: data.kycStatus,
                },
              });
            },
            onError: (e) => {
              setIsLoading(false);
              onError(e as Error);
            },
          }
        );
      }
      // Otherwise, create new org
      else {
        const createData = buildCreateIndividualInvestorOrgData(baseParams);

        createOrgMutation.mutate(createData, {
          onSuccess: (data) => {
            // Sync the newly created organization ID to Redux state
            if (data?.id) {
              dispatch(setOrgId({ orgId: data.id }));
            }
            handleSubmissionSuccess({
              ...successHandlerParams,
              userDetail: {
                ...successHandlerParams.userDetail,
                organization: data.id.toString(),
              },
            });
          },
          onError: (e) => {
            setIsLoading(false);
            onError(e as Error);
          },
        });
      }
    },
    [
      createOrgMutation,
      updateOrgMutation,
      setIsLoading,
      onError,
      handleSubmissionSuccess,
      dispatch,
    ]
  );

  return { submitIndividualInvestorOrganization, handleSubmissionSuccess };
};
